import { fetchEntitlements, getSelectedBoxId } from '../../utils/productLicensing';

const getAuthToken = () => {
  const user = JSON.parse(sessionStorage.getItem('user') || 'null');
  return user && user.signInUserSession && user.signInUserSession.idToken
    ? user.signInUserSession.idToken.jwtToken
    : '';
};

// The stage gateway rejects JSON bodies over 8KB with {"message":"Forbidden"}
// and no CORS headers. The browser then reports "Failed to fetch".
const BODY_BUDGET = 7500;

const gzipBase64 = async (text) => {
  if (typeof window.CompressionStream !== 'function') return '';
  const stream = new Blob([text])
    .stream()
    .pipeThrough(new window.CompressionStream('gzip'));
  const bytes = new Uint8Array(await new Response(stream).arrayBuffer());
  let binary = '';
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000));
  }
  return btoa(binary);
};

const packDraftPayload = async (payload) => {
  if (!payload || payload.html_body == null) return payload;
  if (JSON.stringify(payload).length <= BODY_BUDGET) return payload;
  const html_body_gz = await gzipBase64(String(payload.html_body));
  if (!html_body_gz) return payload;
  const packed = { ...payload, html_body_gz };
  delete packed.html_body;
  if (JSON.stringify(packed).length > BODY_BUDGET) {
    throw new Error(
      'This template is too large to save. Remove extra images or unused sections and try again.'
    );
  }
  return packed;
};

const request = async (path, options = {}) => {
  const response = await fetch(`${process.env.REACT_APP_BASEURL}${path}`, {
    withCredentials: true,
    ...options,
    headers: {
      Authorization: `Bearer ${getAuthToken()}`,
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data.message || 'Request failed');
    error.status = response.status;
    error.data = data;
    throw error;
  }
  return data;
};

export const fetchMessageOverview = (boxId) =>
  request(`api/v1/message-control/overview?box_id=${encodeURIComponent(boxId)}`);

export const fetchMessageTemplates = (boxId, method, eventKey) => {
  const params = new URLSearchParams();
  params.set('box_id', boxId || '');
  params.set('method', method || '');
  if (eventKey) params.set('event_key', eventKey);
  return request(`api/v1/message-control/templates?${params.toString()}`);
};

export const saveMessageDraft = async (payload) => {
  const packed = await packDraftPayload(payload);
  const data = await request('api/v1/message-control/templates', {
    method: 'PUT',
    body: JSON.stringify(packed),
  });
  invalidateTemplateList(payload && payload.channel);
  return data;
};

export const publishMessageTemplates = (payload) =>
  request('api/v1/message-control/templates/publish', {
    method: 'POST',
    body: JSON.stringify(payload),
  }).then((data) => {
    invalidateTemplateList();
    return data;
  });

export const restoreMessageTemplate = (payload) =>
  request('api/v1/message-control/templates/restore', {
    method: 'POST',
    body: JSON.stringify(payload),
  }).then((data) => {
    invalidateTemplateList(payload && payload.channel);
    return data;
  });

const TEMPLATE_LIST_TTL_MS = 30000;
const templateListCache = {};
const templateListInflight = {};

const templateListKey = (channel, boxId) => `${channel || ''}|${boxId || ''}`;

export const fetchTemplateList = (channel, boxId) => {
  const key = templateListKey(channel, boxId);
  const hit = templateListCache[key];
  if (hit && Date.now() - hit.at < TEMPLATE_LIST_TTL_MS) {
    return Promise.resolve(hit.value);
  }
  if (templateListInflight[key]) {
    return templateListInflight[key];
  }
  const params = new URLSearchParams();
  params.set('channel', channel || '');
  if (boxId) params.set('box_id', boxId);
  templateListInflight[key] = request(
    `api/v1/message-control/template-list?${params.toString()}`
  )
    .then((data) => {
      templateListCache[key] = { at: Date.now(), value: data };
      templateListInflight[key] = null;
      return data;
    })
    .catch((err) => {
      templateListInflight[key] = null;
      throw err;
    });
  return templateListInflight[key];
};

export const fetchTemplateAudit = ({ box_id, method, channel, event_key }) => {
  const params = new URLSearchParams();
  params.set('box_id', box_id || '');
  params.set('method', method || '');
  params.set('channel', channel || '');
  if (event_key) params.set('event_key', event_key);
  return request(`api/v1/message-control/template-audit?${params.toString()}`);
};

export const fetchTemplateHtml = (boxId, method, channel, eventKey) => {
  const params = new URLSearchParams();
  params.set('box_id', boxId || '');
  params.set('method', method || '');
  params.set('channel', channel || '');
  if (eventKey) params.set('event_key', eventKey);
  return request(`api/v1/message-control/template-html?${params.toString()}`);
};

const invalidateTemplateList = (channel) => {
  Object.keys(templateListCache).forEach((key) => {
    if (!channel || key.indexOf(`${channel}|`) === 0) {
      delete templateListCache[key];
    }
  });
};

export const createListedTemplate = (payload) =>
  request('api/v1/message-control/templates/create', {
    method: 'POST',
    body: JSON.stringify(payload),
  }).then((data) => {
    invalidateTemplateList(payload && payload.channel);
    return data;
  });

export const setMessageTemplateDefault = (payload) =>
  request('api/v1/message-control/templates/default', {
    method: 'POST',
    body: JSON.stringify(payload),
  }).then((data) => {
    invalidateTemplateList(payload && payload.channel);
    return data;
  });

export const deleteListedTemplate = ({ box_id, method, channel, event_key }) =>
  request(
    `api/v1/message-control/templates?box_id=${encodeURIComponent(
      box_id || ''
    )}&method=${encodeURIComponent(method || '')}&channel=${encodeURIComponent(
      channel || ''
    )}&event_key=${encodeURIComponent(event_key || 'ready')}`,
    { method: 'DELETE' }
  ).then((data) => {
    invalidateTemplateList(channel);
    return data;
  });

export const previewMessageTemplate = (payload) =>
  request('api/v1/message-control/preview', {
    method: 'POST',
    body: JSON.stringify(payload),
  });

export const fetchMessageNotes = (boxId, method) =>
  request(
    `api/v1/message-control/notes?box_id=${encodeURIComponent(
      boxId
    )}&method=${encodeURIComponent(method)}`
  );

export const saveMessageNote = (payload) =>
  request('api/v1/message-control/notes', {
    method: 'PUT',
    body: JSON.stringify(payload),
  });

export const deleteMessageNote = ({ box_id, method, id }) =>
  request(
    `api/v1/message-control/notes?box_id=${encodeURIComponent(
      box_id || ''
    )}&method=${encodeURIComponent(method || '')}&id=${encodeURIComponent(
      id || ''
    )}`,
    { method: 'DELETE' }
  );

export const uploadMessageImage = async (file, boxId) => {
  const data = new FormData();
  data.append('file', file);
  const response = await fetch(
    `${process.env.REACT_APP_BASEURL}api/v1/message-control/upload-image?box_id=${encodeURIComponent(
      boxId || ''
    )}`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
      body: data,
    }
  );
  const result = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(result.message || 'Unable to upload that image.');
  }
  return result.url || result.location;
};

export const fetchMessageAccounts = async () => {
  const accounts = await request('accounts');
  return (Array.isArray(accounts) ? accounts : []).map((account) => ({
    id: account.id,
    name: account.name,
    account_lineage: account.account_lineage,
    brandName:
      account.brand && account.brand.name ? account.brand.name : account.name,
    logoUrl:
      account.brand &&
      account.brand.logo_url &&
      /^https?:\/\//i.test(String(account.brand.logo_url)) &&
      String(account.brand.logo_url).indexOf('NoImageFound') === -1
        ? account.brand.logo_url
        : '',
  }));
};

export const loadMessagingEntitlements = () =>
  fetchEntitlements({ boxId: getSelectedBoxId() });



// WEBPACK FOOTER //
// ./src/components/message-control-center/api.js